// @djsem/analysis-contract — AI Artwork Generation Engine (Prompt 22A).
//
// Provider-agnostic AI artwork generation. Consumes 22C prompt bundles when available;
// otherwise a minimal payload. Every completed job produces DJSEMAssetRecord variants via
// AssetManagerService — never untracked loose files. No provider execution here (rule 3);
// only job planning, credit estimation, provider dispatch, and result → asset attachment.

import { type DJSEMValidationResult, toDJSEMValidationResult, type ValidationResult, stableStringify, fnv1a, type ProviderRegistry } from "@djsem/shared";
import { SeqCounter } from "@djsem/shared";
import type { DJSEMAIPromptBundle } from "./aiPromptBuilder";
import type { DJSEMAssetRecord } from "./assetManager";
import { AssetManagerService } from "./assetManager";

// ---- contracts --------------------------------------------------------------

/** The kind of artwork output an AI artwork request/job/output can produce. */
export type DJSEMAIArtworkOutputType =
  | "release_cover" | "single_cover" | "ep_cover" | "album_cover"
  | "soundcloud_artwork" | "social_square" | "social_story" | "social_post"
  | "event_poster" | "artist_profile_image" | "press_kit_image"
  | "thumbnail" | "campaign_variant" | "brand_exploration";

/** Which kind of artwork generation provider is backing a request (disabled/stub/external). */
export type DJSEMAIArtworkProviderType = "disabled" | "manual_stub" | "external_stability" | "external_openai" | "external_replicate" | "external_custom";

/** Describes what an artwork generation provider supports (output types, formats, limits). */
export interface DJSEMAIArtworkProviderCapabilities {
  providerId: string;
  providerType: DJSEMAIArtworkProviderType;
  supportedOutputTypes: DJSEMAIArtworkOutputType[];
  supportedAspectRatios: string[];
  supportedFormats: Array<"png" | "jpg" | "webp">;
  maxVariantsPerJob: number;
  supportsTransparentBackground: boolean;
  supportsImageReference: boolean;
  supportsBrandReference: boolean;
  maxPromptLength?: number;
  warnings: string[];
}

/** A user/system request to generate AI artwork for a project/release/track/set/brand world. */
export interface DJSEMAIArtworkGenerationRequest {
  id: string;
  projectId: string;
  releaseId?: string; trackId?: string; setId?: string; brandWorldId?: string;
  outputType: DJSEMAIArtworkOutputType;
  promptBundleId?: string;
  userBrief?: string;
  aspectRatio: string;
  width?: number; height?: number;
  format: "png" | "jpg" | "webp";
  variantCount: number;
  qualityMode: "draft" | "standard" | "high";
  providerId?: string;
  userConfirmedRightsWarning: boolean;
  userConfirmedCreditUsage: boolean;
  createdBy?: string;
  createdAt: string;
}

/** Lifecycle status of an AI artwork generation job. */
export type DJSEMAIArtworkJobStatus =
  | "created" | "awaiting_rights_confirmation" | "awaiting_credits"
  | "queued" | "running" | "completed" | "failed" | "cancelled";

/** A queued/running/completed unit of work backing an artwork generation request. */
export interface DJSEMAIArtworkGenerationJob {
  id: string;
  requestId: string;
  projectId: string;
  providerId: string;
  status: DJSEMAIArtworkJobStatus;
  progress: number;
  estimatedCreditCost: number;
  chargedCreditCost?: number;
  outputAssetIds: string[];
  outputVariantIds: string[];
  promptBundleId?: string;
  safetyReportId?: string;
  warnings: string[];
  errors: string[];
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
}

/** A single generated artwork output (one variant of a job), with its rights/review status. */
export interface DJSEMAIArtworkOutput {
  id: string;
  jobId: string;
  assetId: string;
  variantId: string;
  outputType: DJSEMAIArtworkOutputType;
  fileRef: string;
  r2Key?: string;
  width: number;
  height: number;
  format: "png" | "jpg" | "webp";
  providerId: string;
  promptBundleId?: string;
  seed?: string;
  generationMetadata: Record<string, unknown>;
  reviewStatus: "not_reviewed" | "needs_review" | "approved" | "rejected";
  rightsStatus: "unknown" | "owned" | "licensed" | "restricted";
  warnings: string[];
}

/** A provider's estimate of credit cost/duration for a not-yet-run artwork request. */
export interface DJSEMAIArtworkEstimate {
  requestId: string;
  providerId: string;
  estimatedCreditCost: number;
  estimatedDurationMs: number;
  warnings: string[];
}

/** The prompt content sent to a provider for a single artwork generation call. */
export interface DJSEMAIArtworkPromptPayload {
  positivePrompt: string;
  negativePrompt?: string;
  brandWorldSummary?: string;
  releaseContext?: string;
  trackContext?: string;
  styleRules: string[];
  forbiddenElements: string[];
  warnings: string[];
}

// ---- provider interfaces ----------------------------------------------------

/** Normalized input passed to an artwork provider's estimate/generate/validateInput calls. */
export interface DJSEMAIArtworkProviderInput {
  requestId: string; jobId: string;
  outputType: DJSEMAIArtworkOutputType;
  prompt: DJSEMAIArtworkPromptPayload;
  aspectRatio: string; width?: number; height?: number;
  format: "png" | "jpg" | "webp";
  variantCount: number;
  qualityMode: "draft" | "standard" | "high";
  seed?: string;
}

/** The result of a provider's generate() call: produced variants plus warnings/errors. */
export interface DJSEMAIArtworkProviderResult {
  ok: boolean;
  variants: Array<{
    fileRef: string; r2Key?: string;
    width: number; height: number;
    seed?: string;
    generationMetadata: Record<string, unknown>;
  }>;
  chargedCreditCost?: number;
  warnings: string[];
  errors: string[];
}

/** Interface every AI artwork provider (real or stub) must implement. */
export interface DJSEMAIArtworkProvider {
  id: string;
  capabilities: DJSEMAIArtworkProviderCapabilities;
  estimate(input: DJSEMAIArtworkProviderInput): Promise<DJSEMAIArtworkEstimate>;
  generate(input: DJSEMAIArtworkProviderInput): Promise<DJSEMAIArtworkProviderResult>;
  validateInput(input: DJSEMAIArtworkProviderInput): DJSEMValidationResult;
}

/** Criteria used to pick an artwork provider capable of servicing a request. */
export interface SelectAIArtworkProviderInput {
  outputType: DJSEMAIArtworkOutputType;
  aspectRatio?: string; format?: "png" | "jpg" | "webp";
  preferredProviderId?: string;
}

// ---- inputs -----------------------------------------------------------------

/** Input for {@link AIArtworkGenerationService.createRequest}. */
export interface CreateAIArtworkRequestInput extends Omit<DJSEMAIArtworkGenerationRequest, "id" | "createdAt"> {
  id?: string; createdAt?: string;
}
/** Input for {@link AIArtworkGenerationService.estimateRequest}. */
export interface EstimateAIArtworkInput { request: DJSEMAIArtworkGenerationRequest; providerId: string; }
/** Input for {@link AIArtworkGenerationService.createJob}. */
export interface CreateAIArtworkJobInput { request: DJSEMAIArtworkGenerationRequest; providerId: string; estimatedCreditCost: number; createdAt?: string; }
/** Input for {@link AIArtworkGenerationService.queueJob}. */
export interface QueueAIArtworkJobInput { job: DJSEMAIArtworkGenerationJob; }
/** Input for {@link AIArtworkGenerationService.completeJob}. */
export interface CompleteAIArtworkJobInput { job: DJSEMAIArtworkGenerationJob; providerResult: DJSEMAIArtworkProviderResult; completedAt?: string; }
/** Input for {@link AIArtworkGenerationService.failJob}. */
export interface FailAIArtworkJobInput { job: DJSEMAIArtworkGenerationJob; error: string; failedAt?: string; }
/** Input for {@link AIArtworkGenerationService.attachOutputsToAssets}. */
export interface AttachAIArtworkOutputsInput { job: DJSEMAIArtworkGenerationJob; providerResult: DJSEMAIArtworkProviderResult; assetManager: AssetManagerService; createdAt?: string; }

// entitlement/policy inputs (see AIArtworkPolicyService)
/** A user's plan-level entitlements relevant to AI artwork generation. */
export interface AIArtworkEntitlementProfile {
  planLevel: string; renderCreditsRemaining: number; watermarkRequired: boolean;
  maxExportResolution: "720p" | "1080p" | "4k" | "custom";
}
/** Input for {@link AIArtworkPolicyService.checkEntitlements}. */
export interface AIArtworkPolicyInput { request: DJSEMAIArtworkGenerationRequest; profile: AIArtworkEntitlementProfile; }
/** Input for {@link AIArtworkPolicyService.checkCredits}. */
export interface AIArtworkCreditInput { request: DJSEMAIArtworkGenerationRequest; profile: AIArtworkEntitlementProfile; estimatedCredits: number; }
/** Input for {@link AIArtworkPolicyService.estimateCredits}. */
export interface AIArtworkCreditEstimateInput { request: DJSEMAIArtworkGenerationRequest; providerCapabilities: DJSEMAIArtworkProviderCapabilities; }
/** Input for {@link AIArtworkPolicyService.checkRightsAndSafety}. */
export interface AIArtworkRightsSafetyInput { request: DJSEMAIArtworkGenerationRequest; bundle?: DJSEMAIPromptBundle; }

// ---- helpers ----------------------------------------------------------------

const AT = "1970-01-01T00:00:00.000Z";
const _seq = new SeqCounter();

// Quality/size multipliers for credit estimation. Structural dev defaults — NOT pricing.
const QUALITY_COST: Record<"draft" | "standard" | "high", number> = { draft: 1, standard: 2, high: 5 };
const OUTPUT_BASE: Partial<Record<DJSEMAIArtworkOutputType, number>> = {
  release_cover: 2, single_cover: 2, ep_cover: 3, album_cover: 4,
  soundcloud_artwork: 2, social_square: 1, social_story: 1, social_post: 1,
  event_poster: 3, artist_profile_image: 2, press_kit_image: 2,
  thumbnail: 1, campaign_variant: 2, brand_exploration: 2,
};

// ---- disabled + stub providers (rule: no real provider without config) ------

/** Always-fails provider used when AI artwork generation is not enabled in this environment. */
export class DisabledAIArtworkProvider implements DJSEMAIArtworkProvider {
  id = "disabled";
  capabilities: DJSEMAIArtworkProviderCapabilities = {
    providerId: "disabled", providerType: "disabled",
    supportedOutputTypes: [], supportedAspectRatios: [], supportedFormats: [],
    maxVariantsPerJob: 0, supportsTransparentBackground: false,
    supportsImageReference: false, supportsBrandReference: false,
    warnings: ["AI artwork generation is not enabled in this environment."],
  };
  async estimate(): Promise<DJSEMAIArtworkEstimate> { return { requestId: "", providerId: this.id, estimatedCreditCost: 0, estimatedDurationMs: 0, warnings: this.capabilities.warnings }; }
  async generate(): Promise<DJSEMAIArtworkProviderResult> { return { ok: false, variants: [], warnings: [], errors: ["AI artwork generation is disabled."] }; }
  validateInput(): DJSEMValidationResult { return toDJSEMValidationResult({ valid: false, issues: [{ path: "$", message: "provider disabled" }] }, "provider_disabled"); }
}

/** Deterministic placeholder provider for tests/offline UI — produces no real images. */
export class ManualStubAIArtworkProvider implements DJSEMAIArtworkProvider {
  id = "manual_stub";
  capabilities: DJSEMAIArtworkProviderCapabilities = {
    providerId: "manual_stub", providerType: "manual_stub",
    supportedOutputTypes: ["release_cover", "single_cover", "social_square", "social_story", "thumbnail", "campaign_variant", "brand_exploration"],
    supportedAspectRatios: ["1:1", "9:16", "16:9", "4:5", "3:2"],
    supportedFormats: ["png", "jpg", "webp"],
    maxVariantsPerJob: 4, supportsTransparentBackground: true,
    supportsImageReference: false, supportsBrandReference: false, maxPromptLength: 2000,
    warnings: ["Manual stub provider — for tests and honest offline UI only. Produces placeholder metadata, no real images."],
  };
  /**
   * Estimate credit cost for a stub generation call.
   * @param input - provider input describing variant count and quality mode.
   * @returns a deterministic credit/duration estimate.
   */
  async estimate(input: DJSEMAIArtworkProviderInput): Promise<DJSEMAIArtworkEstimate> {
    return { requestId: input.requestId, providerId: this.id, estimatedCreditCost: input.variantCount * (QUALITY_COST[input.qualityMode] ?? 1), estimatedDurationMs: 500 * input.variantCount, warnings: [] };
  }
  /**
   * Produce placeholder variants (no real image generation).
   * @param input - provider input describing job id, format, and variant count.
   * @returns a stub provider result with placeholder file refs.
   */
  async generate(input: DJSEMAIArtworkProviderInput): Promise<DJSEMAIArtworkProviderResult> {
    const variants = Array.from({ length: input.variantCount }, (_, i) => ({
      fileRef: `stub://${input.jobId}/v${i + 1}.${input.format}`,
      width: input.width ?? 1024, height: input.height ?? 1024,
      seed: input.seed ?? `stub_${input.jobId}_${i}`,
      generationMetadata: { stub: true, variantIndex: i, prompt: input.prompt.positivePrompt.slice(0, 200) },
    }));
    return { ok: true, variants, chargedCreditCost: input.variantCount * (QUALITY_COST[input.qualityMode] ?? 1), warnings: this.capabilities.warnings, errors: [] };
  }
  /**
   * Structurally validate a provider input against this stub's capabilities.
   * @param input - provider input to validate.
   * @returns validation result (never throws).
   */
  validateInput(input: DJSEMAIArtworkProviderInput): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (!input.prompt.positivePrompt.trim()) issues.push({ path: "$.prompt.positivePrompt", message: "required" });
    if (input.variantCount < 1 || input.variantCount > this.capabilities.maxVariantsPerJob) issues.push({ path: "$.variantCount", message: `1..${this.capabilities.maxVariantsPerJob}` });
    if (!this.capabilities.supportedOutputTypes.includes(input.outputType)) issues.push({ path: "$.outputType", message: `unsupported by ${this.id}` });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "manual_stub_input");
  }
}

// ---- provider registry ------------------------------------------------------

/** Registry of available AI artwork providers; selects the best match for a request, failing closed to "disabled". */
export class AIArtworkProviderRegistry implements ProviderRegistry<DJSEMAIArtworkProvider, DJSEMAIArtworkProviderCapabilities, SelectAIArtworkProviderInput> {
  private providers = new Map<string, DJSEMAIArtworkProvider>();
  constructor() {
    this.register(new DisabledAIArtworkProvider());
    this.register(new ManualStubAIArtworkProvider());
  }
  /**
   * Register (or replace) a provider by its id.
   * @param p - the provider instance to register.
   */
  register(p: DJSEMAIArtworkProvider): void { this.providers.set(p.id, p); }
  /**
   * Look up a registered provider by id.
   * @param id - provider id.
   * @returns the provider, or undefined if not registered.
   */
  getProvider(id: string): DJSEMAIArtworkProvider | undefined { return this.providers.get(id); }
  /**
   * List capabilities for every registered provider.
   * @returns array of provider capability descriptors.
   */
  listCapabilities(): DJSEMAIArtworkProviderCapabilities[] { return [...this.providers.values()].map((p) => p.capabilities); }
  /**
   * Select the best available provider for the given request criteria.
   * @param input - output type / aspect ratio / format / preferred provider id.
   * @returns the selected provider; falls back to the disabled provider if none match.
   */
  selectProvider(input: SelectAIArtworkProviderInput): DJSEMAIArtworkProvider {
    if (input.preferredProviderId) {
      const p = this.providers.get(input.preferredProviderId);
      if (p && p.capabilities.supportedOutputTypes.includes(input.outputType)) return p;
    }
    for (const p of this.providers.values()) {
      if (p.capabilities.providerType === "disabled") continue;
      if (!p.capabilities.supportedOutputTypes.includes(input.outputType)) continue;
      if (input.aspectRatio && !p.capabilities.supportedAspectRatios.includes(input.aspectRatio)) continue;
      if (input.format && !p.capabilities.supportedFormats.includes(input.format)) continue;
      return p;
    }
    // Fail closed: never silently pick a wrong provider.
    return this.providers.get("disabled")!;
  }
}

// ---- policy service ---------------------------------------------------------

/** Entitlement, credit, and rights/safety gating for AI artwork generation requests. */
export class AIArtworkPolicyService {
  /**
   * Check whether a request is allowed under the user's plan entitlements.
   * @param input - the request and the user's entitlement profile.
   * @returns validation result describing any entitlement violations.
   */
  checkEntitlements(input: AIArtworkPolicyInput): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (input.request.qualityMode === "high" && (input.profile.planLevel === "free_starter"))
      issues.push({ path: "$.qualityMode", message: "high-quality generation requires an upgraded plan" });
    if (input.profile.watermarkRequired && (input.request.outputType === "release_cover" || input.request.outputType === "album_cover"))
      issues.push({ path: "$.outputType", message: "release/album covers require watermark-free plan" });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "ai_artwork_entitlement");
  }

  /**
   * Check whether the user has enough render credits remaining.
   * @param input - the user's profile and the estimated credit cost.
   * @returns validation result (invalid when credits are insufficient).
   */
  checkCredits(input: AIArtworkCreditInput): DJSEMValidationResult {
    if (input.profile.renderCreditsRemaining >= input.estimatedCredits) return toDJSEMValidationResult({ valid: true, issues: [] }, "ai_artwork_credits");
    return toDJSEMValidationResult({ valid: false, issues: [{ path: "$", message: `Insufficient render credits: ${input.profile.renderCreditsRemaining}/${input.estimatedCredits}` }] }, "ai_artwork_credits");
  }

  /**
   * Estimate the credit cost of a request based on output type, quality, and variant count.
   * @param input - the request and provider capabilities.
   * @returns the estimated credit cost (minimum 1).
   */
  estimateCredits(input: AIArtworkCreditEstimateInput): number {
    const base = OUTPUT_BASE[input.request.outputType] ?? 2;
    const q = QUALITY_COST[input.request.qualityMode] ?? 1;
    return Math.max(1, base * q * input.request.variantCount);
  }

  /**
   * Check rights/safety confirmations for a request before generation is allowed.
   * @param input - the request and (optionally) its prompt bundle.
   * @returns validation result describing any missing confirmations or rights warnings.
   */
  checkRightsAndSafety(input: AIArtworkRightsSafetyInput): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (!input.request.userConfirmedRightsWarning) issues.push({ path: "$.userConfirmedRightsWarning", message: "user must confirm generated art is not legally cleared" });
    if (input.bundle?.rightsWarnings?.length) issues.push({ path: "$.bundle.rightsWarnings", message: `bundle carries ${input.bundle.rightsWarnings.length} rights warning(s)` });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "ai_artwork_rights_safety");
  }
}

// ---- main generation service ------------------------------------------------

/** Orchestrates AI artwork requests through validation, estimation, job execution, and asset attachment. */
export class AIArtworkGenerationService {
  constructor(private registry: AIArtworkProviderRegistry, private _policy = new AIArtworkPolicyService()) {}

  /** The policy service used for entitlement/credit/rights gating. */
  get policy(): AIArtworkPolicyService { return this._policy; }

  /**
   * Build a new artwork generation request, filling in id/createdAt defaults.
   * @param input - the request fields (id/createdAt optional).
   * @returns the constructed request.
   */
  createRequest(input: CreateAIArtworkRequestInput): DJSEMAIArtworkGenerationRequest {
    return {
      ...input,
      id: input.id ?? `air_${input.projectId}_${_seq.next()}`,
      createdAt: input.createdAt ?? AT,
    };
  }

  /**
   * Validate a request's required fields and confirmations.
   * @param request - the request to validate.
   * @returns validation result.
   */
  validateRequest(request: DJSEMAIArtworkGenerationRequest): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (!request.projectId) issues.push({ path: "$.projectId", message: "required" });
    if (!request.outputType) issues.push({ path: "$.outputType", message: "required" });
    if (!request.aspectRatio) issues.push({ path: "$.aspectRatio", message: "required" });
    if (request.variantCount < 1) issues.push({ path: "$.variantCount", message: "must be >= 1" });
    if (request.variantCount > 8) issues.push({ path: "$.variantCount", message: "must be <= 8 per request" });
    if (!request.userConfirmedCreditUsage) issues.push({ path: "$.userConfirmedCreditUsage", message: "user must confirm credit usage" });
    if (!request.userConfirmedRightsWarning) issues.push({ path: "$.userConfirmedRightsWarning", message: "user must confirm rights warning" });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "ai_artwork_request");
  }

  /**
   * Ask the selected provider to estimate credit cost/duration for a request.
   * @param input - the request and the provider id to estimate against.
   * @returns the provider's estimate (falls back to the disabled provider if the id is unknown).
   */
  async estimateRequest(input: EstimateAIArtworkInput): Promise<DJSEMAIArtworkEstimate> {
    const provider = this.registry.getProvider(input.providerId) ?? this.registry.getProvider("disabled")!;
    const providerInput: DJSEMAIArtworkProviderInput = {
      requestId: input.request.id, jobId: `est_${input.request.id}`,
      outputType: input.request.outputType,
      prompt: { positivePrompt: input.request.userBrief ?? "", styleRules: [], forbiddenElements: [], warnings: [] },
      aspectRatio: input.request.aspectRatio, width: input.request.width, height: input.request.height,
      format: input.request.format, variantCount: input.request.variantCount, qualityMode: input.request.qualityMode,
    };
    return provider.estimate(providerInput);
  }

  /**
   * Create a job for a request, parked at the appropriate status if rights/credit confirmations are missing.
   * @param input - the request, provider id, and estimated credit cost.
   * @returns the created job.
   */
  createJob(input: CreateAIArtworkJobInput): DJSEMAIArtworkGenerationJob {
    // Rights/credit confirmation gates the job status. A rights-warning-unconfirmed request
    // should never even get here (validateRequest catches it), but if it does, park it.
    const status: DJSEMAIArtworkJobStatus = !input.request.userConfirmedRightsWarning
      ? "awaiting_rights_confirmation"
      : !input.request.userConfirmedCreditUsage
        ? "awaiting_credits"
        : "created";
    return {
      id: `aij_${input.request.id}_${fnv1a(stableStringify({ p: input.providerId, at: input.createdAt ?? AT }))}`,
      requestId: input.request.id, projectId: input.request.projectId,
      providerId: input.providerId, status, progress: 0,
      estimatedCreditCost: input.estimatedCreditCost,
      outputAssetIds: [], outputVariantIds: [],
      promptBundleId: input.request.promptBundleId,
      warnings: [], errors: [],
      createdAt: input.createdAt ?? AT,
    };
  }

  /**
   * Transition a "created" job to "queued".
   * @param input - the job to queue.
   * @returns the (possibly) updated job.
   */
  async queueJob(input: QueueAIArtworkJobInput): Promise<DJSEMAIArtworkGenerationJob> {
    if (input.job.status === "created") return { ...input.job, status: "queued" };
    return input.job;
  }

  /**
   * Mark a job completed or failed based on the provider result.
   * @param input - the job and the provider's generation result.
   * @returns the updated job.
   */
  completeJob(input: CompleteAIArtworkJobInput): DJSEMAIArtworkGenerationJob {
    if (!input.providerResult.ok || input.providerResult.variants.length === 0) {
      return {
        ...input.job, status: "failed", progress: 1,
        errors: [...input.job.errors, ...input.providerResult.errors, "No output asset produced."],
        completedAt: input.completedAt ?? AT,
      };
    }
    return {
      ...input.job, status: "completed", progress: 1,
      chargedCreditCost: input.providerResult.chargedCreditCost ?? input.job.estimatedCreditCost,
      warnings: [...input.job.warnings, ...input.providerResult.warnings],
      completedAt: input.completedAt ?? AT,
    };
  }

  /**
   * Mark a job failed with an explicit error.
   * @param input - the job and the failure reason.
   * @returns the updated (failed) job.
   */
  failJob(input: FailAIArtworkJobInput): DJSEMAIArtworkGenerationJob {
    return { ...input.job, status: "failed", errors: [...input.job.errors, input.error], completedAt: input.failedAt ?? AT };
  }

  // Bridge provider results into AssetManager. Rule: every completed job produces
  // DJSEMAssetRecord entries — no untracked loose files. Rights default 'unknown' + warning.
  /**
   * Attach a job's provider output variants to the asset manager, creating a tracked asset + variant per output.
   * @param input - the job, its provider result, and the asset manager to write into.
   * @returns the created asset records (one per output variant).
   */
  async attachOutputsToAssets(input: AttachAIArtworkOutputsInput): Promise<DJSEMAssetRecord[]> {
    const assets: DJSEMAssetRecord[] = [];
    for (let i = 0; i < input.providerResult.variants.length; i++) {
      const v = input.providerResult.variants[i];
      const asset = input.assetManager.createAsset({
        title: `${input.job.projectId} · ${input.job.id} · v${i + 1}`,
        // "brand_asset" is the correct DJSEMShowPackAssetType for generated artwork (covers,
        // socials, campaign variants). "cover_art" is not in the union.
        assetType: "brand_asset",
        origin: "generated",
        payloadHashSource: v.fileRef,
        createdAt: input.createdAt ?? AT,
      });
      const withVariant = input.assetManager.addVariant({
        asset,
        label: `variant_${i + 1}`,
        payloadHashSource: v.fileRef,
        r2Key: v.r2Key,
        format: "png",
      });
      assets.push(withVariant);
    }
    return assets;
  }
}
